import axiosInstance from '../../../utils/axios';
import { buildCryptoProofFormData } from './cryptoProofUpload';

import {
  BankAccount,
  BankAccountCreationDTO,
  CryptoAccount,
  CryptoAccountCreationDTO,
  CustomApiResponse,
  SubmitCardTokenRequest,
  WithdrawalInitRequestDTO,
  WithdrawalInitResponseDTO,
} from './models';

const handleErrorWithdrawalRequest = (
  error: any,
  onError: (message: string) => void,
): void => {
  console.error('Withdrawal request failed:', error);
  if (error.response?.status === 409) {
    onError('Another user has already used this wallet to make a withdrawal.');
  } else if (error.response?.data?.message) {
      onError(error.response.data.message);
  } else {
    onError('Votre demande a échouée.');
  }
};

class PostWithdrawalRequestService {

  async submitWithdrawalRequest(
    accessToken: string,
    dto: WithdrawalInitRequestDTO,
    onSuccess: (message: string) => void,
    onError: (message: string) => void,
  ): Promise<WithdrawalInitResponseDTO> {
    try {
      const response = await axiosInstance.post<CustomApiResponse<WithdrawalInitResponseDTO>>(
        '/withdrawal/request',
        dto,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        onSuccess('Votre demande a été effectuée avec succès.');
      }
      return response.data.data;
    } catch (error) {
      handleErrorWithdrawalRequest(error, onError);
      throw error;
    }
  }


  async getUserBankAccounts(accessToken: string): Promise<BankAccount[]> {
    const response = await axiosInstance.get<CustomApiResponse<BankAccount[]>>(
      '/bank-accounts',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data.data;
  }

  async createUserBankAccount(
    accessToken: string,
    data: BankAccountCreationDTO,
  ): Promise<BankAccount> {
    const response = await axiosInstance.post<CustomApiResponse<BankAccount>>(
      '/bank-accounts',
      data,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data.data;
  }


  async getUserCryptoAccounts(accessToken: string): Promise<CryptoAccount[]> {
    const response = await axiosInstance.get<CustomApiResponse<CryptoAccount[]>>(
      '/crypto-accounts',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data.data;
  }

  async createUserCryptoAccount(
    accessToken: string,
    data: CryptoAccountCreationDTO,
  ): Promise<CryptoAccount> {
    const response = await axiosInstance.post<CustomApiResponse<CryptoAccount>>(
      '/crypto-accounts',
      data,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data.data;
  }


  /**
   * Uploads the wallet proof (QR screenshot) and returns its file id, which the address creation
   * then references. The part is named `files`, as the gateway requires; it was sent as `file`
   * since the upload API moved from `/api/file` to `/api/files`, so every crypto address creation
   * failed at this step with a generic error.
   */
  async uploadCryptoProof(accessToken: string, file: File): Promise<string> {
    const formData = buildCryptoProofFormData(file);

    const response = await axiosInstance.post<
      CustomApiResponse<Array<{ fileId: string; fileUrl: string; originalFileName: string }>>
    >('/files/upload', formData, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data[0].fileId;
  }

  // BridgerPay methods
  async submitBridgerPayCardToken(
    accessToken: string,
    request: SubmitCardTokenRequest,
  ): Promise<void> {
    await axiosInstance.post<CustomApiResponse<void>>(
      '/withdrawal/submit-card-token',
      request,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
  }
}

const postWithdrawalRequestService = new PostWithdrawalRequestService();
export default postWithdrawalRequestService;